import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DOWNLOAD_ANONYMIZE_LEVELS, DownloadModalTypes, fileTypes, Modules, webMapDownloadFunctions } from "../../../../../constants/WebMap";
import TypeSelect from "../../../TypeSelect";
import { WebMapContext } from "../../../../../pages/sections/web-map/WebMapGoogleMap";
import { getUploadedDataFromFile, getUploadedFiles, getWebMapLayers } from "../../../../../services/api/mapFileUpload.api";
import { toast } from "react-toastify";
import { paginatedApiInvoker } from "../../../../../utils/webMapApiHelper";
import {
  InfoBox,
  Input,
  InputGroup,
  InputWrapper,
  Label,
  LayerTags,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalWrapper,
  RadiusSubtext,
  Row,
  SameOrderSubtext,
  Select,
  SelectAllLayersButton,
  Tag,
} from "./MapDownloadDataModal.styled";
import { checkIntersections } from "../../../../../utils/helper";
import { TOAST_MESSAGES } from "../../../../../constants/General";

const MapDownloadDataModal = ({ isOpen, onClose, type, onSubmit, data }) => {
  const {
    appliedFilters,
    downloadData,
    downloadProperties,
    setDownloadProperties,
    setApisOngoing,
    toolMarkers,
    enabledModules,
    setIsLoading,
    filterOpPermissions,
  } = useContext(WebMapContext);
  const { country, year, quarter } = appliedFilters;

  const radiusInputRef = useRef(null);
  const [formData, setFormData] = useState({
    file: "",
    module: null,
    layers: [],
    selectedLayers: [],
    radius: downloadProperties?.radius
      ? `${(downloadProperties?.radius).toFixed(2)} KM`
      : "",
  });
  const downloadModules = downloadProperties?.downloadModules;
  const [selectedLayer, setSelectedLayer] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [coordinatesFromFile, setCoordinatesFromFile] = useState([]);

  const handleInputChange = (name, value, inputRef) => {
    if (name === "radius") {
      // Remove non-numeric and non-decimal characters
      const numericValue = value.replace(/[^0-9.]/g, "");

      // Ensure only one decimal point is allowed and restrict to two decimal places
      const validValue =
        numericValue.split(".").length > 2
          ? numericValue.slice(0, numericValue.lastIndexOf("."))
          : numericValue;

      const restrictedValue = validValue.includes(".")
        ? validValue.split(".")[0] + "." + validValue.split(".")[1].slice(0, 2)
        : validValue;

      // Prevent values greater than maxRadius
      if (parseFloat(restrictedValue) > data?.maxRadius) {
        return;
      }

      // Get the current cursor position
      const cursorPosition = inputRef.current.selectionStart;

      // If the value is invalid, do not update the state or move the cursor
      if (value.endsWith(" ") || value.match(/[^0-9. KM]/)) {
        return;
      }

      const newValue = restrictedValue ? `${restrictedValue} KM` : " KM";

      setFormData((prev) => ({
        ...prev,
        [name]: newValue,
      }));
      setDownloadProperties({
        ...downloadProperties,
        radius: parseFloat(restrictedValue),
      });

      // Restore the cursor position
      setTimeout(() => {
        const newCursorPosition = Math.min(cursorPosition, newValue.length - 3);
        inputRef.current.setSelectionRange(
          newCursorPosition,
          newCursorPosition
        );
      }, 0);
    } else if (name === "module") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        selectedLayers: [],
        layers: [],
      }));
    } else {

      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const getAnonymizedOperators = async () => {
    const anonymizeLevels = DOWNLOAD_ANONYMIZE_LEVELS[formData?.module];
    
    const permittedOperators = await filterOpPermissions(
      appliedFilters.countryCode,
      anonymizeLevels.levelOne,
      anonymizeLevels.levelTwo,
      anonymizeLevels.levelThee,
      null,
      null,
      true
    );

    const operators = permittedOperators?.map((op) => op.operator);
    const anonymizedOperators = permittedOperators?.map((op) => op?.isAnonymized && op.operator)?.filter(Boolean);
    
    const operatorObject = permittedOperators?.reduce((acc, op) => {
      acc[op.operator] = op.name;
      return acc;
    }, {});

    return { operators, anonymizedOperators, operatorObject };
  };

  const flattenObject = (
    operators,
    anonymizedOperators,
    operatorObject,
    obj,
    parent = "",
    res = {}
  ) => {
    const singleColumns = ["op", "operator"];
    const arrayColumns = ["proposedTenants", "customersOnSite"];

    // Determine if the object contains any anonymized operator
    const isAnonymized = Object.entries(obj).some(([key, value]) => {
      if (arrayColumns.includes(key)) {
        return (
          Array.isArray(value) &&
          value.length > 0 &&
          value.every((item) => anonymizedOperators.includes(item))
        );
      }
      return singleColumns.includes(key) && anonymizedOperators.includes(value);
    });
  
    for (let key in obj) {
      const propName = parent ? `${parent}_${key}` : key;
      let value = obj[key];
  
      if (typeof value === "object" && value !== null && !Array.isArray(value)) {
        // Handle nested objects
        if (isAnonymized && (key === "geometry" || key === "geoPoint")) {
          value = { ...value, coordinates: ["xxx.xxxxx", "x.xxxxx"] };
        }
        res[propName] = JSON.stringify(value);
      } else if (Array.isArray(value)) {
        // Handle arrays
        res[propName] = value
          .map((item) =>
            typeof item === "object"
              ? JSON.stringify(item)
              : operators.includes(item)
              ? operatorObject[item]
              : item
          )
          .join(",");
      } else {
        // Handle primitive values
        if (operators.includes(value)) {
          res[propName] = operatorObject[value];
        } else {
          if (isAnonymized) {
            if (key === "latitude") value = "x.xxxxx";
            if (key === "longitude") value = "xxx.xxxxx";
          }
          res[propName] = value;
        }
      }
    }
  
    return res;
  };  

  const generateCSV = async(data) => {
    try {
      const headers = formData.selectedLayers;
      const { operators, anonymizedOperators, operatorObject } = await getAnonymizedOperators();
      
      const flattenedData = data.map((item) =>
        flattenObject(operators,anonymizedOperators, operatorObject, item)
      );
   
      const csvRows = [];
      csvRows.push(headers.join(","));

      flattenedData.forEach((row) => {
        const rowValues = headers.map((header) => {
          const value = row[header] !== undefined ? row[header] : "";
          return `"${String(value).replace(/"/g, '""')}"`;
        });
        csvRows.push(rowValues.join(","));
      });

      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "map_data_download.csv";
      link.click();
      toast.success("Data file downloaded successfully! ", {
        theme: "colored",
      });
    } catch (error) {
      toast.error(TOAST_MESSAGES.VIEW_BEFORE_DOWNLOAD_DATA, {
        theme: "colored",
      });
    }
  };

  const fetchDownloadData = async () => {
    try {
      const selectedModule = formData?.module;
      const chosenApi = downloadData[selectedModule]?.function || webMapDownloadFunctions[selectedModule];
      const radius = parseFloat(formData?.radius?.replace("KM", "").trim());
      const coordinates = type === DownloadModalTypes.UPLOAD_FILES ? coordinatesFromFile : [toolMarkers];

      // Handle market share download
      if(selectedModule === Modules.MARKET_SHARE_TOP || selectedModule === Modules.MARKET_SHARE_OPERATOR){
        setIsLoading(true);
        const result = await checkIntersections(
          coordinates,
          radius,
          downloadData[selectedModule]
        );
        generateCSV(result);
        return;
      }

      await paginatedApiInvoker(
        (pageSize, page) =>
          chosenApi(
            {
              ...downloadData[selectedModule],
              isDownloadEnabled: true,
              radius: radius,
              coordinates: coordinates,
            },
            downloadData[selectedModule]?.cancelToken,
            pageSize,
            page
          ),
        40000,
        () => setApisOngoing((count) => count + 1),
        (data) => {},
        (data) => {
          generateCSV(data);
          setApisOngoing((count) => count - 1);
        }
      );
    } catch (err) {
      toast.error(TOAST_MESSAGES.VIEW_BEFORE_DOWNLOAD_DATA, {
        theme: "colored",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLayers = useCallback(
    async (module) => {
      setIsLoading(true);
      try {
        const response = await getWebMapLayers(module, `${quarter} ${year}`);

        if (!response?.length) {
          toast.error("No data for this quarter!", { theme: "colored" });
        }

        setFormData((prev) => ({
          ...prev,
          layers: response,
        }));
      } finally {
        setIsLoading(false);
      }
    },
    [appliedFilters, setIsLoading]
  );

  const fetchPolygon = useCallback(
    async (id) => {
      setIsLoading(true);
      try {
        const siteData = await getUploadedDataFromFile(id);

        const dat = siteData?.coordinates?.map((item) => {
          return item.coordinates.map((item) => {
            return {
              lat: item.latitude,
              lng: item.longitude,
            };
          });
        });
        setCoordinatesFromFile(dat);
      } catch (error){
        toast.error("Error while fetching polygon coordinates!", { theme: "colored" });
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading]
  );

  const fetchUploadedFiles = useCallback(
    async () => {
      setIsLoading(true);
      try {
        const updatedFiles = await getUploadedFiles(country, year, quarter);
        const filtered = updatedFiles.filter((file) => file.type===fileTypes.POLYGON)

        if (!filtered?.length) {
          toast.error("No uploaded polygon files for this quarter!", { theme: "colored" });
        }
        
        setUploadedFiles(filtered);
      } finally {
        setIsLoading(false);
      }
    },
    [country, quarter, setIsLoading, year]
  );

  const handleAddLayer = (layerInput) => {
    if (layerInput && !formData.selectedLayers.includes(layerInput)) {
      setFormData((prev) => ({
        ...prev,
        selectedLayers: [...prev.selectedLayers, layerInput],
      }));
    }
  };

  const handleRemoveLayer = (index) => {
    setFormData((prev) => ({
      ...prev,
      selectedLayers: prev.selectedLayers.filter((_, i) => i !== index),
    }));
  };

  const handleSelectAllLayers = () => {
    if (!formData.module) {
      return toast.error("Please select a module!", { theme: "colored" });
    }
    setFormData((prev) => ({
      ...prev,
      selectedLayers: formData?.layers, // Add all layers here.
    }));
  };

  const handleSubmit = () => {
    const { module, selectedLayers, file } = formData;

    if (type === DownloadModalTypes.UPLOAD_FILES && !file) {
      return toast.error("Please select a file!", { theme: "colored" });
    }

    if (!module) {
      return toast.error("Please select a module!", { theme: "colored" });
    }

    if (!selectedLayers?.length) {
      return toast.error("Please select at least one layer!", { theme: "colored" });
    }

    fetchDownloadData();
  };

  const handleLayerSelection = (item) => {
    if (item) {
      handleAddLayer(item);
      setTimeout(() => {
        setSelectedLayer(null);
      }, 5);
    }
  };

  const availableLayers = formData?.layers?.filter(
    (layer) => !formData.selectedLayers.includes(layer)
  );

  useEffect(() => {
    if (formData?.module) {
      const selectedModule = downloadModules?.filter(
        (module) => module?.key === formData?.module
      )[0];
      fetchLayers(selectedModule.collectionName);
    }
  }, [downloadModules, enabledModules, fetchLayers, formData?.module]);

  useEffect(() => {
    if (type === DownloadModalTypes.UPLOAD_FILES) {
      fetchUploadedFiles();
    }
  }, [fetchUploadedFiles, type]);

  useEffect(() => {
    if (formData?.file) {
      fetchPolygon(formData?.file);
    }
  }, [fetchPolygon, formData?.file]);

  if (!isOpen) return null;

  return (
    <ModalWrapper>
      <ModalContent>
        <ModalHeader>
          <span>Download Map Data</span>
          <button onClick={onClose}>&times;</button>
        </ModalHeader>
        <ModalBody>
          {/* Top Row */}
          {type === DownloadModalTypes.PIN && (
            <InputGroup>
              <Label>Radius</Label>
              <Input
                type="text"
                ref={radiusInputRef}
                value={formData.radius}
                onChange={(e) =>
                  handleInputChange("radius", e.target.value, radiusInputRef)
                }
                placeholder="e.g., 3.8 KM"
                width="45%"
              />
              <RadiusSubtext>{`*Radius should not exceed ${downloadProperties?.maximumRadiusDownloadPin} km`}</RadiusSubtext>
            </InputGroup>
          )}
          {type === DownloadModalTypes.UPLOAD_FILES && (
            <InputGroup>
              <Label>File</Label>
              <Select
                value={formData.file}
                onChange={(e) => handleInputChange("file", e.target.value)}
              >
                <option value="">Select File</option>
                {uploadedFiles?.map((item) => {
                return (
                  <option key={item?._id} value={item?._id}>
                    {item?.file_name}
                  </option>
                );
              })}
              </Select>
            </InputGroup>
          )}
          {/* Common Inputs */}
          <InputGroup>
            <Label>Module</Label>
            <Select
              value={formData.module}
              onChange={(e) => handleInputChange("module", e.target.value)}
            >
              <option value="">Select Module</option>
              {downloadModules?.map((item) => {
                return (
                  <option key={item?.key} value={item?.key}>
                    {item?.name}
                  </option>
                );
              })}
            </Select>
          </InputGroup>
          <InputGroup>
            <Row>
              <Label>Layers</Label>
              <SelectAllLayersButton onClick={handleSelectAllLayers}>
                Select All Layers
              </SelectAllLayersButton>
            </Row>
            <div style={{ display: "flex", gap: "8px" }}>
              <InputWrapper>
                <TypeSelect
                  disabled={!formData?.layers?.length}
                  placeholder={"Select Layers..."}
                  options={availableLayers}
                  control={selectedLayer}
                  setControl={handleLayerSelection}
                  usergroup={true}
                  setInputControl={(item) => setSelectedLayer(item)}
                />
              </InputWrapper>
            </div>
            <LayerTags>
              {formData.selectedLayers.map((layer, index) => (
                <Tag key={index}>
                  {layer}
                  <button onClick={() => handleRemoveLayer(index)}>
                    &times;
                  </button>
                </Tag>
              ))}
            </LayerTags>
          </InputGroup>
          {/* Info Section */}
          <InfoBox>
            <InfoOutlinedIcon fontSize="small" />
            <SameOrderSubtext>
              The data columns will be in the same order as your selection
              above.
            </SameOrderSubtext>
          </InfoBox>
        </ModalBody>
        <ModalFooter>
          <button className="cancel" onClick={onClose}>
            Cancel
          </button>
          <button className="download" onClick={handleSubmit}>
            Download
          </button>
        </ModalFooter>
      </ModalContent>
    </ModalWrapper>
  );
};

export default MapDownloadDataModal;
